export interface UserV2AchievementView {
  key: string;
  icon: string;
  name: string;
  // recompensa (título exibido no modal) — nem todo badge define, alguns só
  // marcam progresso/desbloqueio sem prêmio associado
  reward: string;
  description: string;
  claimed: boolean;
  // ação de quando o badge ainda não foi desbloqueado (ex.: 'dp:deposit') —
  // executada via window._smartico.do(...), documentado em TMissionOrBadge
  ctaAction: string;
  ctaText: string;
}

// Texto legível da description: a maioria dos badges chega em texto puro, mas
// os do álbum temático (Copa do Mundo, category_ids [96]) chegam como
// JSON-string com atributos extras de jogador (ver
// StickerDetailModal#parseDescription, que faz o parse completo pra exibir
// esses atributos). Aqui só queremos o texto — não duplicamos aquele parser.
function extractDescriptionText(raw: string): string {
  const trimmed = raw?.trim();

  if (!trimmed?.startsWith('{')) return raw || '';

  try {
    const parsed = JSON.parse(trimmed);

    if (parsed && typeof parsed.Text === 'string') return parsed.Text;
  } catch {
    // não é JSON de verdade — cai pro texto cru
  }

  return raw;
}

// Estado das conquistas do Minha Conta V2 (ENG-362): lista real dos badges do
// CRM (Smartico — mesma fonte de components/smartico/apostatudo/Badges.vue) e
// a conquista selecionada no modal de detalhe. Fica em useState pra persistir
// enquanto o painel troca de tela (dashboard ↔ tela Conquistas).
export default function useUserV2Achievements() {
  const { loadBadges } = useSmarticoBadges();
  const { badges } = storeToRefs(useSmarticoBadges());

  // Espera a API do Smartico ficar funcional antes de buscar (mesmo gate do
  // AlbumCopa.vue). Chamar getBadges cedo demais devolve array vazio, e sem o
  // mock a seção de Conquistas simplesmente não apareceria na sessão.
  const { waitForReady } = useSmarticoUserReady(undefined, ['getBadges']);

  waitForReady(async () => {
    if (badges.value.length === 0) {
      await loadBadges();
    }
  });

  const selectedKey = useState<string | null>(
    'user-v2-achievement-selected',
    () => null,
  );

  const list = computed<UserV2AchievementView[]>(() =>
    badges.value.map((badge) => ({
      key: String(badge.id),
      icon: badge.image,
      name: badge.name,
      reward: badge.reward,
      description: extractDescriptionText(badge.description),
      claimed: badge.is_completed,
      ctaAction: badge.cta_action,
      ctaText: badge.cta_text,
    })),
  );

  const selected = computed<UserV2AchievementView | null>(
    () => list.value.find((a) => a.key === selectedKey.value) || null,
  );

  function openDetail(key: string) {
    selectedKey.value = key;
  }

  function closeDetail() {
    selectedKey.value = null;
  }

  // Badge não tem "resgate" — é o próprio CRM que marca is_completed quando o
  // jogador cumpre a ação. Enquanto bloqueado, o botão do modal dispara a
  // ação sugerida pelo Smartico (cta_action, ex. 'dp:deposit').
  function runCta() {
    if (!selected.value?.ctaAction) return;
    if (typeof window === 'undefined') return;

    window._smartico?.do?.(selected.value.ctaAction);
    closeDetail();
  }

  return {
    list,
    selected,
    openDetail,
    closeDetail,
    runCta,
  };
}
